import _ from 'underscore'
export const alertCodeDisplayValue = (code) => code?.replace(/^CL2./i, 'ERR.')?.replace(/^NDA./i, 'SDA.')
export const alertCodeRawValue = (code) => code?.replace(/^ERR./i, 'CL2.')?.replace(/^SDA./i, 'NDA.')?.toUpperCase() || null

export const INVALID_UNIT_TYPES = ['N/A', 'NA']
export const INVALID_SERIAL_IDS = ['N/A', 'NA', 'NativeRemoteIDTUniqueID', 'InternetODTUniqueID']
export const sortBasedOnSeverity = (alerts) => {
  if (alerts?.length === 1) {
    return alerts
  }

  return alerts?.sort((a, b) => {
    const aSeverity = alertSeverity(a)
    const bSeverity = alertSeverity(b)

    if (aSeverity < bSeverity) {
      return -1
    } else if (aSeverity === bSeverity) {
      return 0
    }

    return 1
  })
}

// descending order
export const sortBasedOnDate = (alerts, attribute) => {
  if (alerts?.length === 1) {
    return alerts
  }

  return alerts?.sort((a, b) => {
    const aMilliseconds = new Date(a[attribute])
    const bMilliseconds = new Date(b[attribute])

    if (aMilliseconds < bMilliseconds) {
      return 1
    } else if (aMilliseconds === bMilliseconds) {
      return 0
    }

    return -1
  })
}

export const updateAlertsSeverity = (alerts) => {
  alerts?.forEach(alert => {
    alert.severity = (alert.code.includes('NDA') || alert.code.includes('HHM') || alert.code.includes('SDA')) && !alert.severity.includes('-sda') ? alert.severity + '-sda' : alert.severity
    const alertSeverity = alert.severity.toLowerCase()
    if (alertSeverity === 'normal') {
      alert.severity = 'minor'
    } else if (alertSeverity === 'normal-sda') {
      alert.severity = 'minor-sda'
    } else {
      alert.severity = alertSeverity
    }
  })

  return alerts
}

export const parseIntoDescriptionsAndDetails = (stringValue) => {
  if (!stringValue || stringValue.length === 0) return []

  const parsedDescriptions = []
  const lines = stringValue.split('\n')

  lines.forEach((line) => {
    const lineIsNewDescription = line.trim().length > 0 && !line.trim().startsWith('-')

    if (lineIsNewDescription) {
      parsedDescriptions.push({ description: line.trim(), details: [] })
    } else if (parsedDescriptions.length > 0) {
      const detail = line.replace(/^-/, '').trim()
      parsedDescriptions[parsedDescriptions.length - 1].details.push(detail)
    }
  })

  return parsedDescriptions
}

export const convertToTextareaValue = (callToAction = []) => {
  return callToAction
    .map(item => {
      const detailsText = (item.details || []).map(detail => `- ${detail}`).join('\n')
      return `${item.description}${detailsText ? '\n' + detailsText : ''}`
    })
    .join('\n')
}

export const highestSeverity = (alerts) => {
  const severityList = _.pluck(alerts, 'severity')

  if (_.contains(severityList, 'critical')) {
    return 'critical'
  } else if (_.contains(severityList, 'critical-sda')) {
    return 'criticalSda'
  } else if (_.contains(severityList, 'major')) {
    return 'major'
  } else if (_.contains(severityList, 'major-sda')) {
    return 'majorSda'
  } else if (_.contains(severityList, 'minor')) {
    return 'minor'
  } else if (_.contains(severityList, 'minor-sda')) {
    return 'minorSda'
  } else {
    return null
  }
}

export const highestSeverityForIcon = (alerts) => {
  const severityList = _.pluck(alerts, 'severity')

  if (_.contains(severityList, 'critical')) {
    return 'critical'
  } else if (_.contains(severityList, 'critical-sda')) {
    return 'critical-sda'
  } else if (_.contains(severityList, 'major')) {
    return 'major'
  } else if (_.contains(severityList, 'major-sda')) {
    return 'major-sda'
  } else if (_.contains(severityList, 'minor')) {
    return 'minor'
  } else if (_.contains(severityList, 'minor-sda')) {
    return 'minor-sda'
  } else {
    return null
  }
}

export const highestSeverityByAlertCount = ({ criticalAlertCount, sdaCriticalAlertCount, majorAlertCount, sdaMajorAlertCount, minorAlertCount, sdaMinorAlertCount }) => {
  // order is important here
  if (criticalAlertCount) return 'critical'
  if (sdaCriticalAlertCount) return 'critical-sda'
  if (majorAlertCount) return 'major'
  if (sdaMajorAlertCount) return 'major-sda'
  if (minorAlertCount) return 'minor'
  if (sdaMinorAlertCount) return 'minor-sda'

  return null
}

const alertSeverity = (alert) => {
  switch (alert.severity) {
    case 'critical':
      return 0
    case 'critical-sda':
      return 1
    case 'major':
      return 2
    case 'major-sda':
      return 3
    case 'minor':
      return 4
    default:
      return 5
  }
}

export const validateAlertDescription = (alertDescription, alertDescriptions) => {
  const errors = {}

  const codeAlreadyExists = _.any(alertDescriptions, (description) => (
    description.code.toUpperCase() === alertDescription.code?.toUpperCase()
  ))

  if (!alertDescription.updatedAt && codeAlreadyExists) {
    errors.code = 'Code is in use by another alert description.'
  }

  if (!alertDescription.code?.match(/^[a-zA-Z0-9]{3}\.\d{3}\.\d{2}$/)) {
    errors.code = 'Code must match format XXX.XXX.XX'
  }

  if (!alertDescription.code) {
    errors.code = 'Code is required.'
  }

  if (!alertDescription.description) {
    errors.description = 'Description is required.'
  }

  if ((alertDescription?.problems ?? [])?.length === 0) {
    errors.problems = 'At least one problem description is required.'
  }

  if ((alertDescription?.possibleCauses ?? [])?.length === 0) {
    errors.possibleCauses = 'At least one possible cause description is required.'
  }

  return errors
}

export const isUnitTypeValid = (unitType, serialId) => {
  return unitType && (unitType !== serialId) && _.all(INVALID_UNIT_TYPES, (invalidUnitType) => {
    return unitType.toLowerCase() !== invalidUnitType.toLowerCase()
  })
}

export const isSerialIdValid = (serialId) => {
  return serialId && _.all(INVALID_SERIAL_IDS, (invalidSerialId) => {
    return serialId.toLowerCase() !== invalidSerialId.toLowerCase()
  })
}

export const removeSeveritylessAlerts = (alerts) => {
  return (alerts ?? []).filter((alert) => typeof alert?.severity === 'string' && alert.severity.trim() !== '')
}
